import { get, post, del } from './apiService';

class NotificationService {
  constructor() {
    this.basePath = '/notifications';
    this._cache = { data: null, expiresAt: 0 };
  }

  /**
   * Get user ID from auth context or in-memory token
   * @returns {string|null} User ID
   */
  getUserId() {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;

      // Decode JWT to get user ID (simple base64 decode of payload)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId || payload.sub || null;
    } catch (error) {
      console.error('Error getting user ID from token:', error);
      return null;
    }
  }

  /**
   * Get notifications for the current user
   * @param {Object} options - Query options
   * @param {boolean} options.unreadOnly - Filter for unread notifications only
   * @param {number} options.limit - Maximum number of notifications to return (max 100)
   * @param {number} options.offset - Offset for pagination
   * @returns {Promise<Object>} Notifications data with pagination info
   */
  async getNotifications({ unreadOnly = false, limit = 100, offset = 0, skipCache = false } = {}) {
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Return cached result if fresh (30s TTL) — prevents redundant fetches on route changes
      const cacheKey = `${userId}_${unreadOnly}_${limit}_${offset}`;
      if (!skipCache && this._cache.key === cacheKey && Date.now() < this._cache.expiresAt) {
        return this._cache.data;
      }

      const params = {
        user_id: userId,
        limit: Math.min(limit, 100),
        offset,
      };

      if (unreadOnly) {
        params.unread_only = true;
      }

      const data = await get(this.basePath, params);
      const result = {
        success: true,
        ...data,
      };

      this._cache = { key: cacheKey, data: result, expiresAt: Date.now() + 30000 };
      return result;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return {
        success: false,
        error: error.message,
        notifications: [],
        pagination: {
          total: 0,
          unread_count: 0,
          limit: 100,
          offset: 0,
          has_more: false,
        },
      };
    }
  }

  /**
   * Mark a single notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Result of the operation
   */
  async markAsRead(notificationId) {
    this._cache = { data: null, expiresAt: 0 };
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const data = await post(this.basePath, {
        user_id: userId,
        notification_ids: [notificationId],
      });

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>} Result of the operation
   */
  async markAllAsRead() {
    this._cache = { data: null, expiresAt: 0 };
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const data = await post(this.basePath, {
        user_id: userId,
        mark_all: true,
      });

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Dismiss (soft delete) a single notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Result of the operation
   */
  async dismissNotification(notificationId) {
    this._cache = { data: null, expiresAt: 0 };
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const data = await del(`${this.basePath}/${notificationId}`, {
        params: { user_id: userId },
      });

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      console.error('Error dismissing notification:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Dismiss (soft delete) specific notifications
   * @param {string[]} notificationIds - Array of notification IDs
   * @returns {Promise<Object>} Result of the operation
   */
  async dismissNotifications(notificationIds) {
    this._cache = { data: null, expiresAt: 0 };
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const data = await post(this.basePath, {
        user_id: userId,
        notification_ids: notificationIds,
        dismiss: true,
      });

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      console.error('Error dismissing notifications:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Dismiss all notifications (clear all)
   * @returns {Promise<Object>} Result of the operation
   */
  async dismissAll() {
    this._cache = { data: null, expiresAt: 0 };
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const data = await post(this.basePath, {
        user_id: userId,
        dismiss_all: true,
      });

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      console.error('Error dismissing all notifications:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get unread count for badge display
   * @returns {Promise<number>} Unread notification count
   */
  async getUnreadCount() {
    try {
      const result = await this.getNotifications({ unreadOnly: true, limit: 1 });
      return result.pagination?.unread_count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
